<template>
  <div class="capacity-overview">
    <ContentWrap class="mb-16px">
      <div class="header-row">
        <div>
          <h2 class="title">配送运营概况</h2>
          <p class="subtitle">今日待办与常用入口</p>
        </div>
        <el-button :loading="loading" @click="load">
          <Icon icon="ep:refresh" class="mr-4px" /> 刷新
        </el-button>
      </div>
    </ContentWrap>

    <!-- 第一行：骑手端装修通栏 → 工作台商城装修 -->
    <ContentWrap title="骑手端装修" class="mb-16px">
      <div class="decorate-card" @click="openDecorate">
        <div class="decorate-card__body">
          <div class="decorate-card__title">骑手端</div>
          <div class="decorate-card__desc">首页 / 大厅等装修</div>
          <button type="button" class="decorate-card__btn" @click.stop="openDecorate">
            去装修
          </button>
        </div>
        <div class="decorate-card__icon">
          <Icon icon="ep:bicycle" :size="56" />
        </div>
      </div>
    </ContentWrap>

    <el-row :gutter="16" class="mb-16px">
      <el-col v-for="card in todoCards" :key="card.key" :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="stat-card" @click="goTo(card.path)">
          <div class="stat-content">
            <div class="stat-icon" :style="{ background: card.bg }">
              <Icon :icon="card.icon" :size="28" :style="{ color: card.color }" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else>{{ card.value }}</span>
              </div>
              <div class="stat-label">{{ card.title }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <ContentWrap title="快捷入口" class="mb-16px">
      <el-row :gutter="16">
        <el-col
          v-for="item in shortcuts"
          :key="item.path"
          :xs="24"
          :sm="12"
          :md="8"
          class="mb-16px"
        >
          <div class="hot-card" @click="goTo(item.path)">
            <div class="hot-card__icon" :style="{ background: item.bg, color: item.color }">
              <Icon :icon="item.icon" :size="26" />
            </div>
            <div class="hot-card__text">
              <div class="hot-card__title">{{ item.title }}</div>
              <div class="hot-card__desc">{{ item.desc }}</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ContentWrap } from '@/components/ContentWrap'
import {
  getCapacityDashboardSummary,
  type CapacityDashboardSummary
} from '@/api/capacity/dashboard'
import { pushDistributionPath } from '@/utils/distributionNavigate'
import { clearStandaloneAppKey } from '@/utils/standaloneApp'

defineOptions({ name: 'CapacityOverview' })

/** 与 remaining.ts WorkbenchMallDecoration 对齐；骑手专属 DIY 后置 */
const DECORATE_PATH = '/template-management/mall'

const router = useRouter()
const loading = ref(false)
const summary = ref<CapacityDashboardSummary>({})

const todoCards = computed(() => {
  const s = summary.value
  return [
    {
      key: 'stuck',
      title: '卡住待办',
      value: s.stuckTodoCount ?? 0,
      icon: 'ep:warning',
      color: '#f56c6c',
      bg: 'rgba(245,108,108,0.12)',
      path: '/distribution/platform/waybill?tab=todos'
    },
    {
      key: 'cert',
      title: '待审认证',
      value: s.pendingCertCount ?? 0,
      icon: 'ep:postcard',
      color: '#e6a23c',
      bg: 'rgba(230,162,60,0.12)',
      path: '/distribution/platform/rider-mgmt?tab=cert'
    },
    {
      key: 'done',
      title: '今日完单',
      value: s.todayCompletedCount ?? 0,
      icon: 'ep:circle-check',
      color: '#67c23a',
      bg: 'rgba(103,194,58,0.12)',
      path: '/distribution/platform/waybill'
    },
    {
      key: 'online',
      title: '在线骑手',
      value: s.onlineRiderCount ?? 0,
      icon: 'ep:bicycle',
      color: '#409eff',
      bg: 'rgba(64,158,255,0.12)',
      path: '/distribution/platform/rider-mgmt'
    }
  ]
})

const shortcuts = [
  {
    title: '模拟造单',
    desc: '联调喂单',
    path: '/distribution/platform/mock',
    icon: 'ep:plus',
    color: '#e85d04',
    bg: 'rgba(232,93,4,0.1)'
  },
  {
    title: '运单巡检',
    desc: '卡住处置 / 指派',
    path: '/distribution/platform/waybill',
    icon: 'ep:list',
    color: '#409eff',
    bg: 'rgba(64,158,255,0.1)'
  },
  {
    title: '骑手',
    desc: '档案与认证审核',
    path: '/distribution/platform/rider-mgmt',
    icon: 'ep:user',
    color: '#6366f1',
    bg: 'rgba(99,102,241,0.1)'
  },
  {
    title: '机构管理',
    desc: '开站 / 清算',
    path: '/distribution/platform/org',
    icon: 'ep:office-building',
    color: '#0d9488',
    bg: 'rgba(13,148,136,0.1)'
  },
  {
    title: '风控处罚',
    desc: '进审与落罚',
    path: '/distribution/platform/penalty',
    icon: 'ep:warning-filled',
    color: '#f56c6c',
    bg: 'rgba(245,108,108,0.1)'
  },
  {
    title: '三角对账',
    desc: '结算总控',
    path: '/distribution/platform/reconcile',
    icon: 'ep:data-analysis',
    color: '#e6a23c',
    bg: 'rgba(230,162,60,0.1)'
  }
]

function goTo(path?: string) {
  if (path) void pushDistributionPath(router, path)
}

/** 新窗口打开装修页；清独立窗 key，避免新页仍套配送壳 */
function openDecorate() {
  const href = new URL(DECORATE_PATH, window.location.origin).href
  const popup = window.open('', '_blank')
  if (!popup) {
    window.open(href, '_blank', 'noopener,noreferrer')
    return
  }
  try {
    clearStandaloneAppKey(popup.sessionStorage)
  } catch {
    // ignore cross-context storage errors
  }
  popup.opener = null
  popup.location.href = href
}

async function load() {
  loading.value = true
  try {
    summary.value = (await getCapacityDashboardSummary()) || {}
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<style scoped lang="scss">
.header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}
.subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.stat-card {
  cursor: pointer;
}
.stat-content {
  display: flex;
  align-items: center;
  gap: 14px;
}
.stat-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}
.stat-label {
  margin-top: 4px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.decorate-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  min-height: 112px;
  padding: 22px 28px;
  border-radius: 12px;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #f97316 0%, #fdba74 100%);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgb(0 0 0 / 12%);
  }
}
.decorate-card__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}
.decorate-card__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
}
.decorate-card__desc {
  font-size: 13px;
  opacity: 0.92;
  line-height: 1.4;
}
.decorate-card__btn {
  margin-top: 6px;
  padding: 6px 16px;
  border: 1px solid rgb(255 255 255 / 55%);
  border-radius: 999px;
  background: rgb(255 255 255 / 22%);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: rgb(255 255 255 / 35%);
  }
}
.decorate-card__icon {
  flex-shrink: 0;
  opacity: 0.92;
  margin-left: 24px;
}
.hot-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--el-border-color-lighter);
  cursor: pointer;
  transition: box-shadow 0.15s ease;
  &:hover {
    box-shadow: var(--el-box-shadow-light);
  }
}
.hot-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hot-card__title {
  font-size: 15px;
  font-weight: 600;
}
.hot-card__desc {
  margin-top: 2px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
</style>
